/**
 * Products Hook using TanStack Query
 * Replaces manual fetching with automatic caching
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { Product, ProductVariant, ProductsResource } from '../../core/resources/products';

import { getGlobalApiClient } from './useApiQuery';
import { usePluginConfig } from './usePluginConfig';

export interface UseProductsQueryOptions {
    productIds?: string[];
    variantIds?: string[];
    enabled?: boolean;
    includeVariants?: boolean;
    includePrices?: boolean;
}

export interface UseProductsQueryResult {
    // Query data
    products: Product[];
    isLoading: boolean;
    error: Error | null;

    // Actions
    refetch: () => Promise<void>;

    // Helper functions
    getProduct: (productId: string) => Product | undefined;
    getVariant: (variantId: string) => { product: Product; variant: ProductVariant } | undefined;
    getAllVariants: () => { product: Product; variant: ProductVariant }[];
    filterVariants: (predicate: (variant: ProductVariant, product: Product) => boolean) => { product: Product; variant: ProductVariant }[];
}

export function useProductsQuery(options: UseProductsQueryOptions = {}): UseProductsQueryResult {
    const { productIds = [], variantIds = [], enabled = true, includeVariants = true, includePrices = true } = options;
    const { storeId } = usePluginConfig();

    // Create products resource client
    const productsResource = useMemo(() => {
        try {
            return new ProductsResource(getGlobalApiClient());
        } catch (error) {
            throw new Error('Failed to initialize products resource: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);

    // Create query key based on options
    const queryKey = useMemo(() =>
        ['products', { storeId, productIds, variantIds, includeVariants, includePrices }],
        [storeId, productIds, variantIds, includeVariants, includePrices]
    );

    // Use TanStack Query for fetching products
    const { data: products = [], isLoading, error, refetch } = useQuery<Product[]>({
        queryKey,
        enabled: enabled && !!storeId,
        queryFn: async () => {
            if (!storeId) {
                throw new Error('Store ID not found. Make sure the TagadaProvider is properly configured.');
            }

            const baseOptions = {
                storeId,
                includeVariants,
                includePrices,
            };

            if (variantIds.length > 0) {
                // Fetch products by variant IDs (most efficient)
                return await productsResource.getProductsByVariantIds(variantIds, baseOptions);
            } else if (productIds.length > 0) {
                // Fetch specific products
                return await productsResource.getProductsByIds(productIds, baseOptions);
            } else {
                // Fetch all products for the store
                return await productsResource.getProducts(baseOptions);
            }
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Helper functions (matching old useProducts exactly)
    const getProduct = useCallback(
        (productId: string): Product | undefined => {
            return products.find((product) => product.id === productId);
        },
        [products],
    );

    const getVariant = useCallback(
        (variantId: string): { product: Product; variant: ProductVariant } | undefined => {
            for (const product of products) {
                const variant = product.variants?.find((v: ProductVariant) => v.id === variantId);
                if (variant) {
                    return { product, variant };
                }
            }
            return undefined;
        },
        [products],
    );

    const getAllVariants = useCallback((): { product: Product; variant: ProductVariant }[] => {
        const allVariants: { product: Product; variant: ProductVariant }[] = [];
        for (const product of products) {
            if (product.variants) {
                for (const variant of product.variants) {
                    allVariants.push({ product, variant });
                }
            }
        }
        return allVariants;
    }, [products]);

    const filterVariants = useCallback(
        (predicate: (variant: ProductVariant, product: Product) => boolean): { product: Product; variant: ProductVariant }[] => {
            const filtered: { product: Product; variant: ProductVariant }[] = [];
            for (const product of products) {
                if (product.variants) {
                    for (const variant of product.variants) {
                        if (predicate(variant, product)) {
                            filtered.push({ product, variant });
                        }
                    }
                }
            }
            return filtered;
        },
        [products],
    );

    return {
        // Query data
        products,
        isLoading,
        error,

        // Actions
        refetch: async () => {
            await refetch();
        },

        // Helper functions
        getProduct,
        getVariant,
        getAllVariants,
        filterVariants,
    };
}
